import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, FormControlLabel, Grid, Switch } from "@mui/material";
import moment from "moment";

import AppForm from "components/Shared/AppForm";
import BaseTextField from "components/Shared/Base/BaseTextField";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import RichTextEditor from "components/Shared/RichTextEditor";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useAuth } from "hooks";
import { formHookInputHelper } from "utils/formHookField";

import { ACCOUNT_LEDGER_ORIGINAL_SOURCE } from "constants/accountLedgerConstants";
import {
  useLazyGetContractorPaymentAccountQuery,
  useLazyGetCustomerPaymentAccountQuery,
  useLazyGetVendorPaymentAccountQuery,
} from "store/services/PaymentsApi/paymentAccountApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useLazyGetDDLContractorListQuery } from "store/services/contractorApi";
import { useLazyGetPrepaymentDDLQuery } from "store/services/PaymentsApi/prepaymentApi";
import { useLazyGetAdjustmentDDLQuery } from "store/services/PaymentsApi/adjustmentApi";
import { useLazyGetDeliveryCollectionDDLQuery } from "store/services/deliveryCollectionApi";
import { useLazyGetDDLCustomerQuery } from "store/services/customerApi";
import { useLazyGetCODTransferDDLQuery } from "store/services/PaymentsApi/codTransferApi";
import { useLazyGetCocCollectionListQuery } from "store/services/cocCollectionApi";

const INITIAL_VALUE = {
  account: "",
  entity: "",
  partyType: null,
  vendor: "",
  contractor: "",
  customer: "",
  transactionDate: "",
  description: "",
  reference: "",
  amount: "",
  type: "",
  originalSource: "",
  addedBy: "",
  isActive: true,
  accountID: "",
  paymentAccount: "",
};

const AccountLedgerForm = ({ accountLedgerDetail, isBusy, onSubmit }) => {
  const { viewEntity, isAccountAdmin, isSystemAdmin, isEntityAdmin } =
    useAuth();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_VALUE,
      account: viewEntity?.entity?.account,
      entity: viewEntity?.entity,
    },
  });

  const [
    fetchVendorPaymentAccount,
    {
      data: vendorPaymentRes,
      isLoading: loadingVendorPayment,
      isFetching: fetchingVendorPayment,
      error: vendorPaymentError,
    },
  ] = useLazyGetVendorPaymentAccountQuery();
  const [
    fetchCustomerPaymentAccount,
    {
      data: customerPaymentRes,
      isLoading: loadingCustomerPayment,
      isFetching: fetchingCustomerPayment,
      error: customerPaymentError,
    },
  ] = useLazyGetCustomerPaymentAccountQuery();
  const [
    fetchContractorPaymentAccount,
    {
      data: contractorPaymentRes,
      isLoading: loadingContractorPayment,
      isFetching: fetchingContractorPayment,
      error: contractorPaymentError,
    },
  ] = useLazyGetContractorPaymentAccountQuery();

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchOriginalSource = useWatch({ control, name: "originalSource" });
  const _watchPartyType = useWatch({ control, name: "partyType" });
  const _watchVendor = useWatch({ control, name: "vendor" });
  const _watchContractor = useWatch({ control, name: "contractor" });
  const _watchCustomer = useWatch({ control, name: "customer" });

  const ORIGINAL_SOURCE_LABELS = {
    [ACCOUNT_LEDGER_ORIGINAL_SOURCE.PREPAYMENT]: "Prepayment",
    [ACCOUNT_LEDGER_ORIGINAL_SOURCE.ADJUSTMENT]: "Adjustment",
    [ACCOUNT_LEDGER_ORIGINAL_SOURCE.DELIVERY_COLLECTION]: "Delivery Collection",
    [ACCOUNT_LEDGER_ORIGINAL_SOURCE.COD_TRANSFER]: "COD Transfer",
    [ACCOUNT_LEDGER_ORIGINAL_SOURCE.COC_COLLECTION]: "COC Collection",
  };
  const {
    data: accounts = [],
    isLoading: loadingAccounts,
    isFetching: fetchingAccounts,
  } = useGetDDLAccountListQuery();

  const [
    fetchEntities,
    {
      data: entities = [],
      isLoading: loadingEntities,
      isFetching: fetchingEntities,
    },
  ] = useLazyGetDDLEntityListQuery();

  const [
    fetchVendors,
    {
      data: vendorList = [],
      isLoading: loadingVendors,
      isFetching: fetchingVendors,
      error: vendorError,
    },
  ] = useLazyGetDdlVendorProfileQuery();
  const [
    fetchContractor,
    {
      data: contractorList = [],
      isLoading: loadingContractor,
      isFetching: fetchingContractor,
      error: contractorError,
    },
  ] = useLazyGetDDLContractorListQuery();
  const [
    fetchCustomer,
    {
      data: customerList = [],
      isLoading: loadingCustomer,
      isFetching: fetchingCustomer,
      error: customerError,
    },
  ] = useLazyGetDDLCustomerQuery();
  const [
    fetchPrepaymentDDL,
    {
      data: prepaymentRes,
      isLoading: loadingPrepayment,
      isFetching: fetchingPrepayment,
    },
  ] = useLazyGetPrepaymentDDLQuery();

  const [
    fetchAdjustmentDDL,
    {
      data: adjustmentRes,
      isLoading: loadingAdjustment,
      isFetching: fetchingAdjustment,
    },
  ] = useLazyGetAdjustmentDDLQuery();
  const [
    fetchDeliveryCollection,
    {
      data: deliveryRes,
      isLoading: loadingDelivery,
      isFetching: fetchingDelivery,
    },
  ] = useLazyGetDeliveryCollectionDDLQuery();

  const [
    fetchCODTransfer,
    { data: codRes, isLoading: loadingCod, isFetching: fetchingCod },
  ] = useLazyGetCODTransferDDLQuery();
  const [
    fetchCoc,
    { data: cocRes, isLoading: loadingCoc, isFetching: fetchingCoc },
  ] = useLazyGetCocCollectionListQuery();
  const isLoadingSourceData =
    loadingPrepayment ||
    fetchingPrepayment ||
    loadingAdjustment ||
    fetchingAdjustment ||
    loadingDelivery ||
    fetchingDelivery ||
    loadingCod ||
    fetchingCod ||
    loadingCoc ||
    fetchingCoc;
  const originSourceRefInput = React.useMemo(() => {
    switch (_watchOriginalSource) {
      case ACCOUNT_LEDGER_ORIGINAL_SOURCE.PREPAYMENT:
        return {
          data: prepaymentRes || [],
          getOptionLabel: (opt) => opt && opt?.prepaymentID,
        };
      case ACCOUNT_LEDGER_ORIGINAL_SOURCE.ADJUSTMENT:
        return {
          data: adjustmentRes || [],
          getOptionLabel: (opt) => opt && opt?.adjustmentID,
        };
      case ACCOUNT_LEDGER_ORIGINAL_SOURCE.DELIVERY_COLLECTION:
        return {
          data: deliveryRes || [],
          getOptionLabel: (opt) => opt && opt?.deliveryCollectionId,
        };
      case ACCOUNT_LEDGER_ORIGINAL_SOURCE.COD_TRANSFER:
        return {
          data: codRes || [],
          getOptionLabel: (opt) => opt && opt?.codTransferId,
        };
      case ACCOUNT_LEDGER_ORIGINAL_SOURCE.COC_COLLECTION:
        let cocData = [];
        if (cocRes) {
          if (Array.isArray(cocRes)) {
            cocData = cocRes;
          } else if (cocRes.data && Array.isArray(cocRes.data)) {
            cocData = cocRes.data;
          } else if (typeof cocRes === "object") {
            cocData = Object.values(cocRes).flat();
          }
        }
        return {
          data: cocData,
          getOptionLabel: (opt) => opt && opt?.cocCollectionId,
        };
      default:
        return { data: [], getOptionLabel: (opt) => opt };
    }
  }, [
    _watchOriginalSource,
    adjustmentRes,
    cocRes,
    codRes,
    deliveryRes,
    prepaymentRes,
  ]);
  // Methods
  const handlePayload = (payload) => {
    const _payload = {
      ...payload,
      account: payload.account._id,
      entity: payload.entity._id,
      vendor: payload.partyType?.value === "vendor" ? payload.vendor : null,
      contractor:
        payload.partyType?.value === "contractor" ? payload.contractor : null,
      customer:
        payload.partyType?.value === "customer" ? payload.customer : null,
    };
    onSubmit(_payload);
  };

  React.useEffect(() => {
    if (!_watchAccount) return;
    fetchEntities({ account: _watchAccount?._id });
  }, [fetchEntities, _watchAccount]);

  React.useEffect(() => {
    if (!_watchEntity?._id || !_watchAccount?._id) return;
    fetchVendors({ account: _watchAccount?._id, entity: _watchEntity?._id });
    fetchContractor({ account: _watchAccount?._id, entity: _watchEntity?._id });
    fetchCustomer({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [
    _watchEntity?._id,
    _watchAccount?._id,
    fetchVendors,
    fetchContractor,
    fetchCustomer,
  ]);

  React.useEffect(() => {
    if (
      _watchOriginalSource !== ACCOUNT_LEDGER_ORIGINAL_SOURCE.PREPAYMENT ||
      !_watchEntity?._id
    )
      return;
    fetchPrepaymentDDL({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchOriginalSource,
    fetchPrepaymentDDL,
  ]);

  React.useEffect(() => {
    if (
      _watchOriginalSource !==
        ACCOUNT_LEDGER_ORIGINAL_SOURCE.DELIVERY_COLLECTION ||
      !_watchEntity?._id
    )
      return;
    fetchDeliveryCollection({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchOriginalSource,
    fetchDeliveryCollection,
  ]);
  React.useEffect(() => {
    if (
      _watchOriginalSource !== ACCOUNT_LEDGER_ORIGINAL_SOURCE.COD_TRANSFER ||
      !_watchEntity?._id
    )
      return;
    fetchCODTransfer({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchOriginalSource,
    fetchCODTransfer,
  ]);
  React.useEffect(() => {
    if (
      _watchOriginalSource !== ACCOUNT_LEDGER_ORIGINAL_SOURCE.COC_COLLECTION ||
      !_watchEntity?._id
    )
      return;
    fetchCoc({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
  }, [_watchAccount?._id, _watchEntity?._id, _watchOriginalSource, fetchCoc]);
  React.useEffect(() => {
    if (
      _watchOriginalSource !== ACCOUNT_LEDGER_ORIGINAL_SOURCE.ADJUSTMENT ||
      !_watchEntity?._id
    )
      return;
    fetchAdjustmentDDL({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchOriginalSource,
    fetchAdjustmentDDL,
  ]);

  React.useEffect(() => {
    if (_watchPartyType?.value === "vendor" && _watchVendor?._id) {
      fetchVendorPaymentAccount({ vendorId: _watchVendor?._id });
    } else if (_watchPartyType?.value === "customer" && _watchCustomer?._id) {
      fetchCustomerPaymentAccount({ customerId: _watchCustomer?._id });
    } else if (
      _watchPartyType?.value === "contractor" &&
      _watchContractor?._id
    ) {
      fetchContractorPaymentAccount({ contractorId: _watchContractor?._id });
    }
  }, [
    _watchPartyType,
    _watchVendor?._id,
    _watchCustomer?._id,
    _watchContractor?._id,
    fetchVendorPaymentAccount,
    fetchCustomerPaymentAccount,
    fetchContractorPaymentAccount,
  ]);

  React.useEffect(() => {
    if (!accountLedgerDetail) return;
    reset({
      ...accountLedgerDetail,
      partyType: accountLedgerDetail.vendor
        ? { label: "Vendor Profile", value: "vendor" }
        : accountLedgerDetail.customer
        ? { label: "Customer Profile", value: "customer" }
        : accountLedgerDetail.contractor
        ? { label: "Contractor Profile", value: "contractor" }
        : null,
    });
  }, [accountLedgerDetail, reset]);

  const partyOptions = [
    { label: "Vendor Profile", value: "vendor" },
    { label: "Customer Profile", value: "customer" },
    { label: "Contractor Profile", value: "contractor" },
  ];

  const getPaymentAccountOptions = () => {
    switch (_watchPartyType?.value) {
      case "vendor":
        return vendorPaymentRes ?? [];
      case "customer":
        return customerPaymentRes ?? [];
      case "contractor":
        return contractorPaymentRes ?? [];
      default:
        return [];
    }
  };

  const getPaymentAccountError = () => {
    switch (_watchPartyType?.value) {
      case "vendor":
        return vendorPaymentError;
      case "customer":
        return customerPaymentError;
      case "contractor":
        return contractorPaymentError;
      default:
        return null;
    }
  };

  const isPaymentAccountLoading = () => {
    switch (_watchPartyType?.value) {
      case "vendor":
        return loadingVendorPayment || fetchingVendorPayment;
      case "customer":
        return loadingCustomerPayment || fetchingCustomerPayment;
      case "contractor":
        return loadingContractorPayment || fetchingContractorPayment;
      default:
        return false;
    }
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={3}>
          {(isAccountAdmin || isSystemAdmin) && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an Account." }}
                  render={(props) => (
                    <BaseAutocomplete
                      {...formHookInputHelper(props)}
                      required
                      fullWidth
                      label="Account"
                      options={accounts ?? []}
                      getOptionLabel={(opt) => opt && opt?.name}
                      isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                      onChange={(_, account) => {
                        setValue("account", account);
                        setValue("entity", "");
                        setValue("partyType", null);
                        setValue("vendor", "");
                        setValue("customer", "");
                        setValue("contractor", "");
                        setValue("paymentAccount", "");
                      }}
                      loading={loadingAccounts || fetchingAccounts}
                      loadingText="Loading Accounts..."
                      disabled={!isSystemAdmin}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an Entity." }}
                  render={(props) => (
                    <BaseAutocomplete
                      {...formHookInputHelper(props)}
                      required
                      fullWidth
                      label="Entity"
                      options={entities ?? []}
                      getOptionLabel={(opt) => opt && opt?.name}
                      isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                      onChange={(_, entity) => {
                        setValue("entity", entity);
                        setValue("partyType", null);
                        setValue("vendor", "");
                        setValue("customer", "");
                        setValue("contractor", "");
                        setValue("paymentAccount", "");
                      }}
                      loading={loadingEntities || fetchingEntities}
                      loadingText="Loading Entities..."
                      disabled={isEntityAdmin}
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={6}>
            <Controller
              name="partyType"
              control={control}
              rules={{ required: "Please select a party type" }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Party Type"
                  value={field.value || null}
                  onChange={(_, data) => {
                    field.onChange(data);
                    setValue("vendor", "");
                    setValue("customer", "");
                    setValue("contractor", "");
                    setValue("paymentAccount", "");
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  options={partyOptions}
                  disabled={!_watchEntity}
                  helperText={errors?.partyType?.message}
                  error={!!errors?.partyType}
                />
              )}
            />
          </Grid>

          {_watchPartyType?.value === "vendor" && (
            <Grid item xs={6}>
              <Controller
                name="vendor"
                control={control}
                rules={{ required: "Please select a Vendor" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Vendor Profile"
                    loading={loadingVendors || fetchingVendors}
                    loadingText="Loading Vendors..."
                    options={vendorList || []}
                    getOptionLabel={(opt) =>
                      opt
                        ? `${opt?.vendor?.name} - ${opt?.phone || "No phone"}`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    value={field.value || null}
                    onChange={(_, data) => {
                      setValue("vendor", data);
                      setValue("paymentAccount", "");
                    }}
                    disabled={
                      !_watchEntity || loadingVendors || fetchingVendors
                    }
                    error={!!errors.vendor || !!vendorError}
                    helperText={
                      errors.vendor?.message ||
                      (vendorError && "Failed to load vendors") ||
                      (vendorList?.length === 0 && "No vendors available")
                    }
                    noOptionsText="No vendors available"
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        {option.vendor?.name} - {option.phone || "No phone"}
                      </li>
                    )}
                  />
                )}
              />
            </Grid>
          )}

          {_watchPartyType?.value === "customer" && (
            <Grid item xs={6}>
              <Controller
                name="customer"
                control={control}
                rules={{ required: "Please select a Customer" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Customer Profile"
                    loading={loadingCustomer || fetchingCustomer}
                    loadingText="Loading Customers..."
                    options={customerList || []}
                    getOptionLabel={(opt) =>
                      opt
                        ? `${
                            opt?.customerUser?.name || opt?.name || "Unknown"
                          } - ${opt?.phone || "No phone"}`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    value={field.value || null}
                    onChange={(_, data) => {
                      setValue("customer", data);
                      setValue("paymentAccount", "");
                    }}
                    disabled={
                      !_watchEntity || loadingCustomer || fetchingCustomer
                    }
                    error={!!errors.customer || !!customerError}
                    helperText={
                      errors.customer?.message ||
                      (customerError && "Failed to load customers") ||
                      (customerList?.length === 0 && "No customers available")
                    }
                    noOptionsText="No customers available"
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        {option.customerUser?.name || option.name || "Unknown"}{" "}
                        - {option.phone || "No phone"}
                      </li>
                    )}
                  />
                )}
              />
            </Grid>
          )}

          {_watchPartyType?.value === "contractor" && (
            <Grid item xs={6}>
              <Controller
                name="contractor"
                control={control}
                rules={{ required: "Please select a Contractor" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Contractor Profile"
                    loading={loadingContractor || fetchingContractor}
                    loadingText="Loading Contractors..."
                    options={contractorList || []}
                    getOptionLabel={(opt) =>
                      opt
                        ? `${
                            opt?.contractor?.name || opt?.name || "Unknown"
                          } - ${opt?.phone || "No phone"}`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    value={field.value || null}
                    onChange={(_, data) => {
                      setValue("contractor", data);
                      setValue("paymentAccount", "");
                    }}
                    disabled={
                      !_watchEntity || loadingContractor || fetchingContractor
                    }
                    error={!!errors.contractor || !!contractorError}
                    helperText={
                      errors.contractor?.message ||
                      (contractorError && "Failed to load contractors") ||
                      (contractorList?.length === 0 &&
                        "No contractors available")
                    }
                    noOptionsText="No contractors available"
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        {option.contractor?.name || option.name || "Unknown"} -{" "}
                        {option.phone || "No phone"}
                      </li>
                    )}
                  />
                )}
              />
            </Grid>
          )}

          {_watchPartyType && (
            <Grid item xs={6}>
              <Controller
                name="paymentAccount"
                control={control}
                rules={{ required: "Please select a Payment Account." }}
                render={(props) => (
                  <BaseAutocomplete
                    {...formHookInputHelper(props)}
                    required
                    fullWidth
                    label="Payment Account"
                    options={getPaymentAccountOptions()}
                    getOptionLabel={(opt) => opt && opt?.accountName}
                    isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                    onChange={(_, data) => {
                      setValue("paymentAccount", data);
                    }}
                    loading={isPaymentAccountLoading()}
                    loadingText="Loading Payment Accounts..."
                    disabled={
                      !_watchPartyType ||
                      (!_watchVendor?._id &&
                        !_watchCustomer?._id &&
                        !_watchContractor?._id)
                    }
                    error={
                      !!errors.paymentAccount || !!getPaymentAccountError()
                    }
                    helperText={
                      errors.paymentAccount?.message ||
                      (getPaymentAccountError() &&
                        "Failed to load payment accounts") ||
                      (getPaymentAccountOptions().length === 0 &&
                        "No payment accounts available")
                    }
                    noOptionsText="No payment accounts available"
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        {option.accountName}
                      </li>
                    )}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Controller
              name="transactionDate"
              control={control}
              rules={{ required: "Please provide Transaction Date" }}
              render={(props) => (
                <BaseTextField
                  fullWidth
                  required
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Transaction Date"
                  {...formHookInputHelper(props)}
                  value={moment(props.field?.value).format("YYYY-MM-DD")}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="reference"
              control={control}
              rules={{ required: "Please provide Reference" }}
              render={(props) => (
                <BaseTextField
                  fullWidth
                  required
                  label="Reference"
                  {...formHookInputHelper(props)}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="amount"
              control={control}
              rules={{
                required: "Please provide Amount",
                validate: (value) => {
                  const amount = parseFloat(value);
                  if (amount <= 0) {
                    return "Amount must be greater than zero.";
                  }
                  return true;
                },
              }}
              render={(props) => (
                <BaseTextField
                  fullWidth
                  required
                  type="number"
                  label="Amount"
                  {...formHookInputHelper(props)}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Please provide Type" }}
              render={(props) => (
                <BaseSelect
                  fullWidth
                  required
                  label="Type"
                  {...formHookInputHelper(props)}
                  options={["Debit", "Credit"]}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="originalSource"
              control={control}
              rules={{ required: "Please provide Original source" }}
              render={(props) => (
                <BaseSelect
                  fullWidth
                  required
                  type="text"
                  label="Original Source"
                  {...formHookInputHelper(props)}
                  options={Object.values(ACCOUNT_LEDGER_ORIGINAL_SOURCE)}
                  getOptionLabel={(value) =>
                    ORIGINAL_SOURCE_LABELS[value] || value
                  }
                  onChange={(e) => {
                    setValue("originalSource", e.target.value);
                    setValue("originalSourceRef", "");
                  }}
                />
              )}
            />
          </Grid>

          {_watchOriginalSource && (
            <Grid item xs={6}>
              <Controller
                name="originalSourceRef"
                control={control}
                rules={{ required: "This is required" }}
                render={(props) => (
                  <BaseAutocomplete
                    required
                    fullWidth
                    label={
                      ORIGINAL_SOURCE_LABELS[_watchOriginalSource] ||
                      _watchOriginalSource
                    }
                    loading={isLoadingSourceData}
                    loadingText={`Loading ${
                      ORIGINAL_SOURCE_LABELS[_watchOriginalSource] ||
                      _watchOriginalSource
                    }...`}
                    options={originSourceRefInput?.data || []}
                    getOptionLabel={originSourceRefInput?.getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    {...formHookInputHelper(props)}
                    disabled={!_watchEntity || isLoadingSourceData}
                    onChange={(_, data) => {
                      setValue("originalSourceRef", data);
                    }}
                    noOptionsText={`No ${
                      ORIGINAL_SOURCE_LABELS[_watchOriginalSource] ||
                      _watchOriginalSource
                    } available`}
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        {originSourceRefInput.getOptionLabel(option)}
                      </li>
                    )}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={12}>
            <div
              style={{ color: "gray", fontSize: 15, marginBottom: ".75rem" }}
            >
              Description
            </div>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required" }}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    accountLedgerDetail?.description
                      ? accountLedgerDetail.description
                      : "<p>Enter Payment Account Detail Description.</p>"
                  }
                  onChange={onChange}
                  height={200}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={field.value === "Active"}
                      onChange={(e) =>
                        field.onChange(e.target.checked ? "Active" : "Inactive")
                      }
                    />
                  }
                  label="Is Active?"
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handlePayload)}
        >
          {accountLedgerDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default AccountLedgerForm;
